import React, { useState, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { useAuth } from '@/hooks/useAuth'
import { useInstructorAssignedModules, useCourse, useUpdateCourse } from '@/hooks'
import { uploadService } from '@/services/uploadService'
import { ImageWithFallback } from '@/components/common/ImageWithFallback'

interface AssignedModule {
  id: string
  courseId: string
  title: string
  description: string
  orderIndex: number
  durationHours: number
  videoCount: number
  quizCount: number
  isDraft: boolean
  createdAt: string
  updatedAt: string
}

export const InstructorCourseDetailPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>()
  const { user } = useAuth()

  const { data: course, isLoading: courseLoading } = useCourse(courseId || '')
  const updateCourse = useUpdateCourse()
  const {
    data: modules,
    isLoading: modulesLoading,
    error,
  } = useInstructorAssignedModules(user?.id || '', courseId || '')

  // Thumbnail upload state
  const [isUploadingThumbnail, setIsUploadingThumbnail] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleThumbnailUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !courseId) return

    setIsUploadingThumbnail(true)
    try {
      const res = await uploadService.uploadImage(file)
      if (!res.success) throw new Error(res.error?.message || 'Failed to upload image')

      await updateCourse.mutateAsync({
        id: courseId,
        data: { thumbnailUrl: res.data!.url }
      })
    } catch (error) {
      alert(error instanceof Error ? error.message : 'An error occurred during upload')
    } finally {
      setIsUploadingThumbnail(false)
    }
  }

  const isLoading = courseLoading || modulesLoading

  return (
    <div>
      <PageHeader
        title={course?.title || 'Course Modules'}
        subtitle="Modules assigned to you in this course"
        breadcrumbs={[
          { label: 'My Courses', to: '/instructor/courses' },
          { label: course?.title || 'Course' }
        ]}
      />

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="mt-4 text-gray-500 dark:text-text-muted">Loading modules...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-6 text-center">
          <span className="material-symbols-outlined text-red-400 text-4xl mb-3">error</span>
          <p className="text-red-400 font-medium text-lg">Failed to load modules</p>
          <p className="text-red-400/70 text-sm mt-2">{error.message}</p>
        </div>
      )}

      {/* Course Info Card */}
      {!isLoading && course && (
        <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-5 mb-6">
          <div className="flex items-start gap-4">
            <div 
              className={`relative w-28 h-18 rounded-lg overflow-hidden bg-slate-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 shrink-0 cursor-pointer group ${isUploadingThumbnail ? 'opacity-50' : 'hover:border-primary/50'}`}
              onClick={() => !isUploadingThumbnail && fileInputRef.current?.click()}
            >
              <ImageWithFallback
                src={course.thumbnailUrl}
                alt={course.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white">
                <span className="material-symbols-outlined text-[18px]">photo_camera</span>
                <span className="text-[8px] font-black uppercase tracking-widest mt-0.5">Ganti Sampul</span>
              </div>
              {isUploadingThumbnail && (
                <div className="absolute inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center">
                  <div className="animate-spin size-5 border-2 border-primary border-t-transparent rounded-full" />
                </div>
              )}
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*"
                onChange={handleThumbnailUpload}
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span
                  className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold uppercase ${
                    course.courseType === 'hybrid' ? 'bg-indigo-500 text-white' : 'bg-emerald-500 text-white'
                  }`}
                >
                  {course.courseType === 'hybrid' ? 'Hybrid' : 'Online'}
                </span>
                {course.price > 0 && (
                  <span className="text-xs text-gray-500 dark:text-text-muted">
                    Rp {course.price.toLocaleString('id-ID')}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 dark:text-text-muted line-clamp-2">
                {course.description}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && (!modules || modules.length === 0) && (
        <div className="text-center py-12 text-gray-500 dark:text-text-muted">
          <div className="size-24 rounded-full bg-white dark:bg-surface-card mx-auto mb-4 flex items-center justify-center">
            <span className="material-symbols-outlined text-[40px] text-gray-500 dark:text-gray-400">folder_off</span>
          </div>
          <p className="text-gray-900 dark:text-white font-bold mb-2 text-lg">No modules assigned</p>
          <p className="text-sm">You haven't been assigned to any modules in this course yet.</p>
          <p className="text-xs mt-4 text-gray-500 dark:text-gray-400">Contact the admin to get module assignments.</p>
        </div>
      )}

      {/* Modules List */}
      {!isLoading && !error && modules && modules.length > 0 && (
        <div className="space-y-4">
          {(modules as AssignedModule[]).map((module) => (
            <div
              key={module.id}
              className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-5 hover:border-primary/20 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 flex-1">
                  <div className="size-14 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <span className="material-symbols-outlined text-primary text-2xl">folder</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                        Module {module.orderIndex + 1}
                      </span>
                      <span
                        className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold uppercase ${
                          module.isDraft
                            ? 'bg-amber-500 text-white'
                            : 'bg-emerald-500 text-white'
                        }`}
                      >
                        {module.isDraft ? 'Draft' : 'Published'}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 truncate">{module.title}</h3>
                    <p className="text-sm text-gray-500 dark:text-text-muted line-clamp-2 mb-3">
                      {module.description || 'No description'}
                    </p>
                    <div className="flex items-center gap-6 text-xs text-gray-500 dark:text-text-muted">
                      <div className="flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-[16px]">play_circle</span>
                        <span>{module.videoCount} videos</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-[16px]">quiz</span>
                        <span>{module.quizCount} quizzes</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-[16px]">schedule</span>
                        <span>{module.durationHours}h</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Link
                    to={`/instructor/modules/${module.id}/edit`}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary-hover transition-colors"
                  >
                    <span className="material-symbols-outlined text-[18px]">edit</span>
                    Edit Module
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Card */}
      <div className="mt-8 rounded-lg bg-blue-500/10 border border-blue-500/20 p-5">
        <div className="flex items-start gap-4">
          <span className="material-symbols-outlined text-blue-400 text-2xl shrink-0">info</span>
          <div>
            <h4 className="text-blue-400 font-bold mb-1">Module Editing Guidelines</h4>
            <ul className="text-blue-400/80 text-sm space-y-1">
              <li>- You can only edit modules that are assigned to you</li>
              <li>- Changes are saved as drafts until admin publishes them</li>
              <li>- Upload videos, PDFs, and other materials for each lesson</li>
              <li>- Contact admin if you need access to additional modules</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default InstructorCourseDetailPage
